import React, { useEffect, useRef, useState } from "react";

/**
 * JerichoTerminalWindow — the hero secure console.
 * Heavy CRT monitor chrome with corner bolts, internal scanlines,
 * teal screen glow, "ON AIR" lamp, and a live transmission log.
 */
export const JerichoTerminalWindow = () => {
  const [lines, setLines] = useState([]);
  const linesRef = useRef(null);

  useEffect(() => {
    const seed = [
      "> handshake … OK",
      "> route via STN-7 / STN-4 / STN-1",
      "> resolving node JERICHO·01 …",
      "> clearance · OMEGA verified",
      "> mounting drawer vault … OK",
      "> awaiting operator directive",
    ];
    let i = 0;
    setLines([]);
    const id = setInterval(() => {
      i += 1;
      setLines(seed.slice(0, i));
      if (i >= seed.length) {
        clearInterval(id);
        startAmbient();
      }
    }, 260);

    const ambientTimers = [];
    const startAmbient = () => {
      const pool = [
        "> packet · 1f4 inbound · accepted",
        "> cross-ref · CASE-08 pending operator",
        "> redaction queue · 14 items",
        "> dead drop · STN-9 confirmed",
        "> source-credibility pass · running",
        "> trace · masked / routed",
        "> evidence · lattice integrity OK",
        "> archive · drift Δ 0.002",
      ];
      const tick = () => {
        const t = setTimeout(() => {
          setLines((prev) => {
            const next = [...prev, pool[Math.floor(Math.random() * pool.length)]];
            return next.slice(-14);
          });
          tick();
        }, 1800 + Math.random() * 1800);
        ambientTimers.push(t);
      };
      tick();
    };

    return () => {
      clearInterval(id);
      ambientTimers.forEach((t) => clearTimeout(t));
    };
  }, []);

  useEffect(() => {
    if (linesRef.current) linesRef.current.scrollTop = linesRef.current.scrollHeight;
  }, [lines]);

  return (
    <section
      data-testid="jericho-terminal-window"
      className="relative"
    >
      {/* Outer monitor housing (dark brushed-metal feel) */}
      <div
        className="relative p-3 sm:p-4 border border-[#1a1815]"
        style={{
          background:
            "linear-gradient(155deg, #1a1714 0%, #0c0a08 35%, #08070a 70%, #0c0a08 100%)",
          boxShadow:
            "0 60px 80px -30px rgba(0,0,0,0.95), 0 0 0 1px rgba(217,45,32,0.10), 0 0 140px rgba(217,45,32,0.10), inset 0 0 30px rgba(0,0,0,0.6)",
        }}
      >
        {/* Corner bolts */}
        <Bolt position="tl" />
        <Bolt position="tr" />
        <Bolt position="bl" />
        <Bolt position="br" />

        {/* Top monitor strip */}
        <div className="flex items-center justify-between px-2 pb-2 mb-2 border-b border-[#2a2520]">
          <div className="flex items-center gap-2">
            {/* ON AIR lamp */}
            <span
              className="block w-2 h-2 rounded-full bg-[#D92D20]"
              style={{ boxShadow: "0 0 8px rgba(217,45,32,0.9), 0 0 18px rgba(217,45,32,0.5)" }}
            />
            <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20]">
              ON AIR · SECURE
            </span>
            <span className="hidden sm:inline font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
              · NODE-01
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
            <span>0x4F2A·B7</span>
            <span className="text-[#5DD4B3]">● CONNECTED</span>
          </div>
        </div>

        {/* Inner screen (the CRT) */}
        <div
          className="relative border-2 border-[#0a0907]"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, #0a1614 0%, #060a0a 60%, #02060a 100%)",
            boxShadow:
              "inset 0 0 60px rgba(0,0,0,0.95), inset 0 0 18px rgba(93,212,179,0.10), 0 0 80px rgba(93,212,179,0.06)",
          }}
        >
          {/* Screen scanlines */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none opacity-[0.16]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(to bottom, rgba(93,212,179,0.6) 0px, rgba(93,212,179,0.6) 1px, transparent 1px, transparent 3px)",
            }}
          />
          {/* Screen vignette */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{ boxShadow: "inset 0 0 120px 30px rgba(0,0,0,0.85)" }}
          />
          {/* Screen tint */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 50% 0%, rgba(217,45,32,0.06) 0%, transparent 50%)",
            }}
          />

          {/* Top corner ticks inside screen */}
          <CornerTicks color="#D92D20" />

          <div className="relative px-5 sm:px-8 pt-6 pb-5">
            {/* Classification stamps */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20] border border-[#D92D20] px-2 py-0.5">
                // CLASSIFIED — INTERNAL ACCESS ONLY
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#5DD4B3] border border-[#5DD4B3]/40 px-2 py-0.5">
                CLEARANCE · VERIFIED
              </span>
              <span className="hidden sm:inline font-mono text-[9px] uppercase tracking-[0.35em] text-[#A39E93] border border-[#454038] px-2 py-0.5">
                NODE 01 · JERICHO
              </span>
            </div>

            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20] mb-2">
              // THE ARCHIVE · INTERNAL NODE
            </div>
            <h1
              className="font-display text-[40px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.92] mb-4 jericho-glitch break-words"
              data-testid="jericho-title"
              style={{
                textShadow:
                  "0 2px 30px rgba(0,0,0,0.85), 0 0 60px rgba(217,45,32,0.30), 0 0 120px rgba(217,45,32,0.18)",
                color: "#F2EBDB",
              }}
            >
              NODE&nbsp;JERICHO
            </h1>
            <p className="max-w-2xl text-[13px] sm:text-sm text-[#CBB892] leading-relaxed border-l-2 border-[#D92D20] pl-3 italic">
              You&rsquo;ve passed the inner door. What sits behind it is unsorted, unfinished,
              and unindexed — working material the editors haven&rsquo;t decided what to do
              with yet. Nothing here is sourced to the public archive. Treat everything as
              a sketch.
            </p>

            {/* Transmission log */}
            <div className="mt-5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#5DD4B3]">
                  // TRANSMISSION LOG
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
                  STREAM 0x1F·LIVE
                </div>
              </div>
              <div
                ref={linesRef}
                className="relative border border-[#1f1d1a] bg-black/70 px-3 py-2 h-[150px] sm:h-[170px] overflow-y-auto font-mono text-[11px] leading-relaxed text-[#7a8294]"
                data-testid="jericho-transmission-log"
                style={{
                  boxShadow: "inset 0 0 30px rgba(0,0,0,0.85), inset 0 0 4px rgba(93,212,179,0.15)",
                }}
              >
                {lines.map((l, i) => (
                  <div key={i} className={i === lines.length - 1 ? "text-[#5DD4B3]" : "text-[#7a8294]"}>
                    {l}
                  </div>
                ))}
                <div className="text-[#D92D20]"><span className="animate-pulse">▌</span></div>
              </div>
            </div>
          </div>

          {/* Bottom corner ticks */}
          <CornerTicks color="#5DD4B3" bottom />

          {/* Bottom monitor info strip */}
          <div className="relative flex items-center justify-between px-3 py-1.5 border-t border-[#0a1614] bg-[#020405] font-mono text-[8.5px] uppercase tracking-[0.35em] text-[#456861]">
            <span>VID-OUT · NTSC·R</span>
            <span className="text-[#5DD4B3]/80">PHOSPHOR · P31</span>
            <span>76.4 Hz · 1024×768</span>
          </div>
        </div>

        {/* Monitor brand plate */}
        <div className="flex items-center justify-between px-1 pt-2 mt-2 border-t border-[#1a1815]">
          <span className="font-mono text-[8.5px] uppercase tracking-[0.4em] text-[#3a342c]">
            JERICHO·MFG // CRT-7B
          </span>
          <span className="font-mono text-[8.5px] uppercase tracking-[0.4em] text-[#3a342c]">
            SN · 04-71-A0
          </span>
        </div>
      </div>

      <style>{`
        @keyframes jerichoGlitch {
          0%, 96%, 100% { transform: translate(0,0); text-shadow: 0 2px 30px rgba(0,0,0,0.85), 0 0 60px rgba(217,45,32,0.30); }
          97% { transform: translate(-0.5px, 0); text-shadow: -1px 0 rgba(217,45,32,0.6), 1px 0 rgba(93,212,179,0.4); }
          98% { transform: translate(0.5px, 0); text-shadow: 1px 0 rgba(217,45,32,0.6), -1px 0 rgba(93,212,179,0.4); }
        }
        .jericho-glitch { animation: jerichoGlitch 6.5s infinite; }
        @media (prefers-reduced-motion: reduce) {
          .jericho-glitch { animation: none; }
        }
      `}</style>
    </section>
  );
};

const Bolt = ({ position }) => {
  const map = {
    tl: "top-1.5 left-1.5",
    tr: "top-1.5 right-1.5",
    bl: "bottom-1.5 left-1.5",
    br: "bottom-1.5 right-1.5",
  };
  return (
    <span
      aria-hidden="true"
      className={`absolute ${map[position]} block w-2 h-2 rounded-full`}
      style={{
        background:
          "radial-gradient(circle at 30% 30%, #4a423a 0%, #1a1612 60%, #050403 100%)",
        boxShadow: "inset 0 0 1px rgba(0,0,0,0.9), 0 0 1px rgba(255,255,255,0.05)",
      }}
    />
  );
};

const CornerTicks = ({ color, bottom = false }) => {
  const pos = bottom ? "bottom-1 left-1 right-1" : "top-1 left-1 right-1";
  return (
    <div className={`absolute ${pos} flex justify-between pointer-events-none`}>
      <span className="block w-3 h-3 border-l border-t" style={{ borderColor: color, opacity: 0.7, transform: bottom ? "rotate(-90deg)" : "" }} />
      <span className="block w-3 h-3 border-r border-t" style={{ borderColor: color, opacity: 0.7, transform: bottom ? "rotate(90deg)" : "" }} />
    </div>
  );
};

export default JerichoTerminalWindow;
